import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import {
  notificationsApi,
  type NotificationTargetType,
  type SendNotificationResult,
} from "../api/notificationsApi";

interface ClientOption {
  id: string;
  name: string;
}

interface SendNotificationTabProps {
  isCentralAdmin: boolean;
  /** Clients the sender may target (central: all clients; client admin: their own). */
  clientsList: ClientOption[];
}

export const SendNotificationTab: React.FC<SendNotificationTabProps> = ({
  isCentralAdmin,
  clientsList,
}) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [targetType, setTargetType] = useState<NotificationTargetType>(
    isCentralAdmin ? "all" : "client",
  );
  const [clientId, setClientId] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendNotificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetOptions = useMemo(
    () =>
      isCentralAdmin
        ? [
            { value: "all", label: "All users (every client)" },
            { value: "client", label: "A specific client" },
          ]
        : [{ value: "client", label: "My client's users" }],
    [isCentralAdmin],
  );

  const needsClient = targetType === "client" && (isCentralAdmin || clientsList.length > 1);

  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    !sending &&
    (!needsClient || clientId);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const target =
        targetType === "all"
          ? { type: "all" as const }
          : {
              type: "client" as const,
              // Client admins with a single client can omit it (server pins to own).
              ...(clientId ? { client_id: clientId } : {}),
            };
      const res = await notificationsApi.send({
        title: title.trim(),
        body: body.trim(),
        target,
      });
      setResult(res.data);
      setTitle("");
      setBody("");
    } catch (e: any) {
      setError(
        e?.response?.data?.message ?? "Failed to send notification. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <SettingsSection>
      <Alert severity="info">
        Sends a push notification to recipients' mobile devices. Only users who
        have installed the app and enabled notifications will receive it.
      </Alert>

      <SectionCard title="Send push notification">
        <Stack spacing={2}>
          <TextField
            label="Title"
            size="small"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 255 }}
            required
          />
          <TextField
            label="Message"
            size="small"
            fullWidth
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={3}
            required
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="notif-target-label">Send to</InputLabel>
              <Select
                labelId="notif-target-label"
                label="Send to"
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value as NotificationTargetType);
                  setClientId("");
                }}
              >
                {targetOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {needsClient && (
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="notif-client-label">Client</InputLabel>
                <Select
                  labelId="notif-client-label"
                  label="Client"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                >
                  {clientsList.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity={result.success > 0 ? "success" : "warning"}>
              Sent to {result.recipients} device{result.recipients === 1 ? "" : "s"} —{" "}
              {result.success} delivered, {result.failure} failed.
              {result.recipients === 0 &&
                " No registered devices matched this audience."}
            </Alert>
          )}

          <Box>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              disabled={!canSend}
              onClick={handleSend}
            >
              {sending ? "Sending…" : "Send notification"}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Tip: keep titles short. The message body is shown in the notification
            tray and on the lock screen.
          </Typography>
        </Stack>
      </SectionCard>
    </SettingsSection>
  );
};
